"use client";

import { motion } from "motion/react";
import {
  type ElementType,
  type JSX,
  memo,
} from "react";

export type TextShimmerProps = {
  children: string;
  as?: ElementType;
  className?: string;
  duration?: number;
  /**
   * Optional CSS gradient overriding the default grey sweep — e.g. the
   * agent-trace orange gradient from the Figma mock.
   */
  gradient?: string;
};

/**
 * Active agent-step title gradient (Figma "loading text", #F34D13 sweep).
 * Exported so trace components and hosts can share the exact same look.
 */
export const AGENT_SHIMMER_GRADIENT =
  "linear-gradient(90deg, rgba(243, 77, 19, 0.25) 0%, rgb(243, 77, 19) 25%, rgb(243, 77, 19) 75%, rgba(243, 77, 19, 0.25) 100%)";

const ShimmerComponent = ({
  children,
  as: Component = "p",
  className,
  duration = 2,
  gradient = "linear-gradient(90deg, #666 0%, #c6c2c2ff 50%, #666 100%)",
}: TextShimmerProps) => {
  const MotionComponent = motion.create(
    Component as keyof JSX.IntrinsicElements
  );

  return (
    <MotionComponent
      className={`relative inline-block ${className || ""}`}
      style={{
        background: gradient,
        backgroundSize: "200% auto",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
      }}
      animate={{ backgroundPosition: ["200% 0", "-200% 0"] }}
      transition={{
        repeat: Number.POSITIVE_INFINITY,
        duration: duration || 4,
        ease: "linear",
      }}
    >
      {children}
    </MotionComponent>
  );
};

export const Shimmer = memo(ShimmerComponent);